import { useEffect, useRef } from 'react';

import styles from './ConfettiCelebration.module.css';

const COLORS = [
  '#ffd23f',
  '#f5b700',
  '#39c56b',
  '#16a765',
  '#2684ff',
  '#56b4ff',
  '#ff8a34',
  '#ef4444',
];
const DURATION_MS = 4_800;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  width: number;
  height: number;
  angle: number;
  spin: number;
  sway: number;
  swaySpeed: number;
  launchAt: number;
  color: string;
  shape: 0 | 1 | 2;
}

interface ConfettiCelebrationProps {
  /** Changing this value starts a fresh burst; null leaves no canvas mounted. */
  runId: number | null;
  onComplete?: () => void;
  /** An admin explicitly requesting a preview has opted into seeing motion. */
  preview?: boolean;
}

interface PerformanceNavigator extends Navigator {
  deviceMemory?: number;
  connection?: { saveData?: boolean };
}

const random = (min: number, max: number) => min + Math.random() * (max - min);
const smoothstep = (from: number, to: number, value: number) => {
  const progress = Math.min(1, Math.max(0, (value - from) / (to - from)));
  return progress * progress * (3 - 2 * progress);
};

/**
 * Canvas-based corner cannons: cheap enough for Telegram's mobile webview and
 * independent of the DOM below, so scrolling and buying stay interactive.
 */
export function ConfettiCelebration({ runId, onComplete, preview = false }: ConfettiCelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (
      runId === null ||
      (!preview && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      if (runId !== null) onComplete?.();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d', { alpha: true, desynchronized: true });
    if (!context) {
      onComplete?.();
      return;
    }

    let width = window.innerWidth;
    let height = window.innerHeight;
    let pixelRatio = 1;
    let frame = 0;
    let previous = performance.now();
    const startedAt = previous;
    let completed = false;
    let activeCount = 0;
    let sampledFrames = 0;
    let sampledTime = 0;
    let qualityReduced = false;

    const performanceNavigator = navigator as PerformanceNavigator;
    const lowPowerDevice =
      performanceNavigator.connection?.saveData === true ||
      (performanceNavigator.deviceMemory !== undefined && performanceNavigator.deviceMemory <= 4) ||
      (performanceNavigator.hardwareConcurrency !== undefined &&
        performanceNavigator.hardwareConcurrency <= 4);

    const complete = () => {
      if (completed) return;
      completed = true;
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      onComplete?.();
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      // A full-screen transparent canvas gets expensive quadratically with
      // pixel ratio. Confetti does not need text-level sharpness, so 1.5x is
      // visually crisp while avoiding a 4x buffer on high-DPI phones.
      pixelRatio = Math.min(window.devicePixelRatio || 1, lowPowerDevice ? 1 : 1.5);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = lowPowerDevice ? 72 : width < 420 ? 104 : 128;
    activeCount = count;
    const particles: Particle[] = Array.from({ length: count }, (_, index) => {
      const fromLeft = index % 2 === 0;
      return {
        x: fromLeft ? random(-8, 16) : random(width - 16, width + 8),
        y: height + random(2, 34),
        vx: (fromLeft ? 1 : -1) * random(2.8, 7.8),
        vy: -random(15.5, 22.5),
        gravity: random(0.16, 0.225),
        width: random(5, 10),
        height: random(9, 17),
        angle: random(0, Math.PI * 2),
        spin: random(-0.19, 0.19),
        sway: random(0, Math.PI * 2),
        swaySpeed: random(0.035, 0.085),
        launchAt: random(0, 520),
        color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
        shape: Math.floor(Math.random() * 3) as Particle['shape'],
      };
    });

    const draw = (now: number) => {
      if (completed) return;
      const elapsed = now - startedAt;
      const frameTime = now - previous;
      const deltaFrames = Math.min(2, frameTime / (1000 / 60));
      previous = now;

      // Measure only after launch settles, then make one downward adjustment
      // if this webview cannot hold roughly 45fps. Never ramp back up mid-run,
      // which would produce a visible pop.
      if (!qualityReduced && elapsed > 650 && sampledFrames < 30) {
        sampledFrames += 1;
        sampledTime += frameTime;
        if (sampledFrames === 30 && sampledTime / sampledFrames > 22) {
          activeCount = Math.max(56, Math.floor(activeCount * 0.7));
          qualityReduced = true;
        }
      }

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);

      const progress = Math.min(1, elapsed / DURATION_MS);
      const morph = smoothstep(0.7, 1, progress);
      const opacity = 1 - smoothstep(0.8, 1, progress);
      context.globalAlpha = opacity;

      for (let index = 0; index < activeCount; index += 1) {
        const particle = particles[index]!;
        if (elapsed < particle.launchAt) continue;

        particle.sway += particle.swaySpeed * deltaFrames;
        particle.vy += particle.gravity * deltaFrames;
        particle.vx *= Math.pow(0.996, deltaFrames);
        particle.x += (particle.vx + Math.sin(particle.sway) * 0.42) * deltaFrames;
        particle.y += particle.vy * deltaFrames;
        particle.angle += particle.spin * deltaFrames;

        const drawWidth = particle.width + (3 - particle.width) * morph;
        const drawHeight = particle.height + (3 - particle.height) * morph;
        const scale = 1 - morph * 0.76;

        // One transform replaces translate + rotate + scale + save/restore for
        // each particle, which is materially cheaper on Android WebViews.
        const cosine = Math.cos(particle.angle) * scale * pixelRatio;
        const sine = Math.sin(particle.angle) * scale * pixelRatio;
        context.setTransform(
          cosine,
          sine,
          -sine,
          cosine,
          particle.x * pixelRatio,
          particle.y * pixelRatio,
        );
        context.fillStyle = particle.color;

        if (morph > 0.65 || particle.shape === 1) {
          context.beginPath();
          context.arc(0, 0, Math.max(drawWidth, drawHeight) / 2, 0, Math.PI * 2);
          context.fill();
        } else if (particle.shape === 2) {
          context.rotate(Math.PI / 4);
          context.fillRect(-drawWidth / 2, -drawWidth / 2, drawWidth, drawWidth);
        } else {
          context.fillRect(-drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        }
      }

      if (progress < 1) {
        frame = window.requestAnimationFrame(draw);
      } else {
        complete();
      }
    };

    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        window.cancelAnimationFrame(frame);
        complete();
      }
    };

    frame = window.requestAnimationFrame(draw);
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', stopWhenHidden);
    };
  }, [runId, onComplete, preview]);

  return runId === null ? null : (
    <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
  );
}
